import { zodResolver } from "@hookform/resolvers/zod";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilePresentOutlinedIcon from "@mui/icons-material/FilePresentOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Grid, IconButton, Typography, styled } from "@mui/material";
import { filesize } from "filesize";
import { t } from "i18next";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import useFileService from "../../services/fileService";
import { useBankingImportFileMutation } from "../../services/bankingService";
import { z } from "zod";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export function FileComponent(props) {
    return (
        <Grid container justifyItems={"center"} justifyContent={"center"} sx={props.sx}>
            <Grid container direction={"row"} borderRadius={2} bgcolor={"white"} xs={10}>
                <Grid
                    container
                    xs={1.1}
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{
                        borderRadius: 100,
                        bgcolor: "whitesmoke",
                        aspectRatio: 1,
                        m: 1,
                    }}
                >
                    <FilePresentOutlinedIcon />
                </Grid>
                <Grid
                    container
                    xs={8.5}
                    direction={"column"}
                    alignItems={"start"}
                    justifyContent={"space-between"}
                    sx={{ m: 1 }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            textWrap: "nowrap",
                            maxWidth: "100%",
                        }}
                    >
                        {props.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{}}>
                        {props.fileSize}
                    </Typography>
                </Grid>
                <Grid
                    container
                    xs={1}
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{ m: 1 }}
                >
                    <IconButton onClick={props.onDelete}>
                        <DeleteOutlineOutlinedIcon color="error" />
                    </IconButton>
                </Grid>
            </Grid>
        </Grid>
    );
}

export function BankingImportListDialog(props) {
    const { control, handleSubmit, watch } = useForm({
        resolver: zodResolver(z.object({})),
    });

    const [uploadedFile, setUploadedFile] = useState(null);

    const btnEnabled =  uploadedFile;

    const { mutate: importFile, isPending } = useBankingImportFileMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("bankings.fileAdded")} />,
                {
                    position: "bottom-right",
                },
            );
            props.onClose();
        },
        onError: (error) => {
            console.log(error, "import error");
            toast.custom(
                () => <SnackbarError message={t("bankings.fileError")} />,
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const onSubmit = useCallback(
        (formValue) => {
            if (!uploadedFile) {
                return;
            }
            importFile({ file: uploadedFile});
        },
        [uploadedFile, importFile],
    );

    const { downloadFile } = useFileService();
    const importTemplateUrl = `${window._env_.OLIVIA_CRM_API_URL}/bankings/bankings/import-template`;

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("bankings.importBanking")}
            maxWidth={"sm"}
            showDialogActions
        >
            <Grid container direction={"column"}>
                <Grid
                    container
                    minHeight={200}
                    sx={{
                        justifyContent: "center",
                        border: 1,
                        borderRadius: 3,
                        borderColor: "whitesmoke",
                        bgcolor: "whitesmoke",
                        boxShadow: "inset 0px 0px 15px rgba(0,0,0,0.05)",
                    }}
                >
                    <Grid
                        container
                        direction={"column"}
                        bgcolor={""}
                        sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                        {!uploadedFile && (
                            <Button
                                component="label"
                                variant="contained"
                                tabIndex={-1}
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("bankings.fileUpload")}
                                <VisuallyHiddenInput
                                    type="file"
                                    onChange={(event) => {
                                        console.log(event.target.files);
                                        setUploadedFile(event.target.files?.item(0) || null);
                                    }}
                                />
                            </Button>
                        )}
                        {!!uploadedFile && (
                            <FileComponent
                                fileName={uploadedFile.name}
                                fileSize={filesize(uploadedFile.size)}
                                onDelete={() => setUploadedFile(null)}
                            />
                        )}
                        <Typography variant="body2" sx={{ color: "gray", mt: 2 }}>
                            {t("bankings.maxFileSize")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
                            {t("bankings.maxNameLength")}
                        </Typography>
                        <Button
                            variant="text"
                            startIcon={<FileDownloadOutlinedIcon />}
                            sx={{ mt: 1 }}
                            size="small"
                            onClick={() =>
                                downloadFile(importTemplateUrl, "rachunki-import-template.xlsx")
                            }
                        >
                            {t("bankings.downloadTemplate")}
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 4 }}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        loading={isPending}
                        onClick={handleSubmit(onSubmit)}
                        disabled={!btnEnabled}
                    >
                        {t("bankings.importButton")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
